import { Component, Input, OnChanges, OnInit, QueryList, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RisikoKfzComponent } from '../risikokfz/risikokfz.component';
import { KaskoKfzComponent } from '../kaskokfz/kaskokfz.component';
import { IKfzResult, IKfzSparteResult } from 'src/app/models/kfzresult';
import { AbstractControl } from '@angular/forms';
import { getMotorrad } from 'src/shared/utils';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { DataService } from '../../../service/data.service';

interface IHaftpflichtUebersicht {
  variante: string;
  versicherungssumme: string;
  bmIntern: string;
  fahrzeugart: string;
  marke: string;
  modell: string;
  antriebsart: string;
  hybridart: number;
  hubraumCcm: number;
  leistungKw: number;
  eigengewichtKg: number;
  erstzulassung: Date;
  co2: number;
  hoechstgeschwindigkeitKmh: number;
  anzahlPlaetze: number;
  nutzlast: number;
  gesamtgewicht: number;
  geburtsdatum: Date;
  geburtsdatumZweiterZulassungsbesitzer: Date;
  region: string;
  bruttoPraemie: number;
  risikoIndex: number;
}

interface IKaskoUebersicht {
  variante: string;
  info: string;
  selbstbehaltVariante: string;
  selbstbehalthoehe: Observable<number>;
  listenpreis: number;
  sonderausstattung: number;
  fahrzeugzustand: string;
  leasingfahrzeug: boolean;
  leasingklausel: boolean;
  neuwertentschaedigung: boolean;
  eVorsorgepaket: boolean;
  vorsteuerabzugsberechtigt: boolean;
  bruttoPraemie: number;
  marke: string;
  modell: string;
  risikoIndex: number;
}

interface IInsassenUebersicht {
  variante: string;
  versicherungssummen: IInsassenUebersichtVs;
  bruttoPraemie: number;
  risikoIndex: number;
  marke: string;
  modell: string;
}

interface IInsassenUebersichtVs {
  todesfall: number;
  invaliditaet: number;
  heilkosten: number;
}

interface MotorsteuerUebersicht {
  risikoIndex: number;
  motorsteuer: number;
  marke: string;
  modell: string;
  motorsteuerFolgt: boolean;
}

@Component({
  selector: 'app-berechnung-table',
  templateUrl: './berechnung-table.component.html',
  styleUrls: ['./berechnung-table.component.scss'],
})
export class BerechnungTableComponent implements OnInit, OnChanges {
  @Input() zahlungsart: AbstractControl;
  @Input() kfzResult: IKfzResult;
  @Input() showMbvInfoWohnMobile: boolean;
  @Input() bmStufeIntern: AbstractControl;
  @Input() geburtsdatum: AbstractControl;
  @Input() geburtsdatumZweiterZulassungsbesitzer: AbstractControl;
  @Input() region: AbstractControl;
  @Input() kaskoKfzComponents: QueryList<KaskoKfzComponent>;
  @Input() risikoKfzComponents: QueryList<RisikoKfzComponent>;
  @Input() insassenVariante: AbstractControl;
  @Input() lenkerVariante: AbstractControl;
  @Input() hpVersicherungssummeVariante: AbstractControl;
  @Input() isPrivatperson: boolean;
  @Input() isEinpersonenUnternehmen: boolean;
  @Input() isFirma: boolean;

  haftpflichtUebersicht: IHaftpflichtUebersicht;
  kaskoUebersicht: Array<IKaskoUebersicht>;
  insassenUebersicht: IInsassenUebersicht;
  motorsteuerUebersicht: MotorsteuerUebersicht;
  showZugmaschineInfo: boolean;
  showInsassen = false;
  motorrad: boolean;
  private readonly fahrzeugartKeysvarianteA = ['15', '22', '6'];

  constructor(public translateService: TranslateService, private route: ActivatedRoute, public dataService: DataService) {
    this.motorrad = getMotorrad(route);
  }


  get co2AusstossMessTyp(): string {
    return this.motorrad ? ' g/km gem. WMTC' : ' g/km gem. WLTP'
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kfzResult'] && !changes['kfzResult'].firstChange) {
      this.setKaskoUebersicht();
      this.setHaftpflichtUebersicht();
      this.setInsassenUebersicht();
      this.setMotorsteuerUebersicht();
    }
  }
  
  ngOnInit(): void {
    this.setKaskoUebersicht();
    this.setHaftpflichtUebersicht();
    this.setInsassenUebersicht();
    this.setZugmaschineInfo();
    this.setMotorsteuerUebersicht();
  }
  
  setMotorsteuerUebersicht() {
    const motorsteuerSparte: IKfzSparteResult = [...(this.kfzResult?.Sparten || [])]?.find(x => x.IsHaftpflicht
      && (x.MotorSteuer > 0 || x.MotorsteuerFolgt));
    if (motorsteuerSparte) {
      const motorsteuerRisikoIndex: number = motorsteuerSparte.RisikoIndex;
      this.motorsteuerUebersicht = {
        risikoIndex: motorsteuerRisikoIndex,
        marke: this.getMarke(motorsteuerRisikoIndex),
        modell: this.getModell(motorsteuerRisikoIndex),
        motorsteuer: motorsteuerSparte.MotorSteuer,
        motorsteuerFolgt: motorsteuerSparte.MotorsteuerFolgt
      }
    }
  }
  
  setInsassenUebersicht() {
    //keine IU
    if ([...(this.kfzResult?.Sparten || [])].filter(x => x.IsInsassen).length < 1) {
      this.showInsassen = false;
      return;
    }
    const bruttoPraemie: number = [...this.kfzResult.Sparten].find(s => s.IsInsassen).BruttoPraemie;
    const risikoIndex: number = [...this.kfzResult.Sparten].find(s => s.IsInsassen).RisikoIndex;
    const marke = this.getMarke(risikoIndex);
    const modell = this.getModell(risikoIndex);
    let variante: string;
    let versicherungssummen: IInsassenUebersichtVs;
    const varianteValue = this.insassenVariante.value?.Value ?? this.lenkerVariante.value?.Value;
    switch (varianteValue) {
      case 'Alle Insassen (Pauschalsystem)':
        variante = 'Alle Insassen Pauschalsystem';
        versicherungssummen = {
          todesfall: 15000,
          invaliditaet: 50000,
          heilkosten: 1000,
        };
        break;
      case 'Bis zu 5 Plätze':
        variante = 'Alle Insassen, Platzsystem bis 5 Plätze';
        versicherungssummen = {
          todesfall: 15000,
          invaliditaet: 50000,
          heilkosten: 1000,
        };
        break;
      case 'Bis zu 9 Plätze':
        variante = 'Alle Insassen, Platzsystem bis 9 Plätze';
        versicherungssummen = {
          todesfall: 15000,
          invaliditaet: 50000,
          heilkosten: 1000,
        };
        break;
      case 'Lenkerunfallschutz':
        variante = 'Lenkerplatz';
        versicherungssummen = {
          todesfall: 3634,
          invaliditaet: 36336,
          heilkosten: null,
        };
        break;
      case 'Top Lenkerunfallschutz':
        variante = 'Lenkerplatz';
        versicherungssummen = {
          todesfall: 7267,
          invaliditaet: 72673,
          heilkosten: null,
        };
        break;
      default:
        return 'Unknown Key';
    }
    this.insassenUebersicht = {
      variante,
      versicherungssummen,
      bruttoPraemie: bruttoPraemie,
      risikoIndex: risikoIndex,
      marke: marke,
      modell: modell,
    };
    this.showInsassen = true;
  }

  setKaskoUebersicht(): void {
    const kaskoResults = [...(this.kfzResult?.Sparten || [])].filter(x => x.IsKasko).sort((a, b) => a.RisikoIndex - b.RisikoIndex);
    this.kaskoUebersicht?.length > 0 ? this.kaskoUebersicht.length = 0 : this.kaskoUebersicht = new Array<IKaskoUebersicht>();
    for (const kaskoResult of kaskoResults) {
      const i = kaskoResult.RisikoIndex;
      const component = this.kaskoKfzComponents.toArray()[i];
      this.kaskoUebersicht.push({
        variante: component.kaskoVariante.value ? component.kaskoVariante.value.Value : null,
        info: this.motorrad ? ' (' + component.getKaskoInfoText(component.kaskoVariante.value.Key) + ')' : null,
        selbstbehaltVariante: component.showOaemtcInfoForKaskoVariante ? component.preselectSbVariante?.Value : component.selbstbehaltVariante.value
          ? component.selbstbehaltVariante.value.Value : null,
        selbstbehalthoehe: component.selbstbehalthoehe,
        listenpreis: component.listenpreis.value ? component.listenpreis.value : null,
        sonderausstattung: component.sonderausstattung.value ? component.sonderausstattung.value : null,
        fahrzeugzustand: component.fahrzeugZustand.value ?
           component.fahrzeugZustand.value === 'ja' ? 'Neufahrzeug' : 'Gebrauchtfahrzeug (Besichtigung erforderlich)' : null,
        leasingfahrzeug: component.leasingFahrzeug.value ? component.leasingFahrzeug.value === 'ja' : null,
        leasingklausel: component.leasingklausel.value ? component.leasingklausel.value === 'ja' : null,
        neuwertentschaedigung: component.neuwertentschaedigung.value ? component.neuwertentschaedigung.value === 'ja' : null,
        eVorsorgepaket: component.evorsorgepaket.value ? component.evorsorgepaket.value === 'ja' && component.showEVorsorgepaket : null,
        vorsteuerabzugsberechtigt: component.vorsteuerabzugsberechtigt.value ? component.vorsteuerabzugsberechtigt.value === 'ja' : null,
        bruttoPraemie: this.kfzResult.Sparten.filter(x => x.IsKasko)[i].BruttoPraemie,
        marke: this.getMarke(i),
        modell: this.getModell(i),
        risikoIndex: i
      });
    }
  }

  getMarke(risikoIndex: number) {
    const component = this.risikoKfzComponents.toArray()[risikoIndex];
    return component?.sonstigeMarke?.valid ? component.sonstigeMarke.value : component?.marke?.value?.Value;
  }

  getModell(risikoIndex: number) {
    return this.risikoKfzComponents.toArray()[risikoIndex].modell.value;
  }

  setHaftpflichtUebersicht() {
    const haftpflichtResult = [...(this.kfzResult?.Sparten || [])].find(x => x.IsHaftpflicht && x.MotorSteuer === 0);
    if (!haftpflichtResult) {
      this.haftpflichtUebersicht = null;
      return;
    }
    const risikoComponent = this.risikoKfzComponents.toArray()[haftpflichtResult.RisikoIndex];
    const fahrzeugart = risikoComponent.fahrzeugart.value;
    this.haftpflichtUebersicht = {
      variante: this.getHaftpflichtvariante(fahrzeugart.Key),
      versicherungssumme: this.hpVersicherungssummeVariante?.value.Value,
      bmIntern: this.bmStufeIntern.value ? this.bmStufeIntern.value.Value : null,
      fahrzeugart: fahrzeugart.Value,
      marke: this.getMarke(haftpflichtResult.RisikoIndex),
      modell: this.getModell(haftpflichtResult.RisikoIndex),
      antriebsart: risikoComponent.antriebsart.value ? risikoComponent.antriebsart.value.Value : risikoComponent.andereAntriebsart.value,
      hybridart: risikoComponent.hybridart.value ? Number(risikoComponent.hybridart.value) : null,
      hubraumCcm: risikoComponent.hubraumCcm.value ? risikoComponent.hubraumCcm.value : null,
      leistungKw: risikoComponent.leistungKw.value ? risikoComponent.leistungKw.value : null,
      eigengewichtKg: risikoComponent.eigengewichtKg.value ? risikoComponent.eigengewichtKg.value : null,
      erstzulassung: risikoComponent.erstzulassungsdatum.value ? risikoComponent.erstzulassungsdatum.value : null,
      co2: risikoComponent.emissionCo2.value ? risikoComponent.emissionCo2.value : null,
      hoechstgeschwindigkeitKmh: risikoComponent.hoechstGeschwindigkeitKmh.value ? risikoComponent.hoechstGeschwindigkeitKmh.value : null,
      anzahlPlaetze: risikoComponent.anzahlPlaetze.value ? risikoComponent.anzahlPlaetze.value : null,
      nutzlast: risikoComponent.nutzlastKg.value ? risikoComponent.nutzlastKg.value : null,
      gesamtgewicht: risikoComponent.gesamtgewichtKg.value ? risikoComponent.gesamtgewichtKg.value : null,
      geburtsdatum: this.geburtsdatum.value ? this.geburtsdatum.value : null,
      geburtsdatumZweiterZulassungsbesitzer: this.geburtsdatumZweiterZulassungsbesitzer.value ? this.geburtsdatumZweiterZulassungsbesitzer.value : null,
      region: this.region.value ? this.region.value.Value : null,
      bruttoPraemie: haftpflichtResult.BruttoPraemie,
      risikoIndex: [...this.kfzResult.Sparten].find(x => x.IsHaftpflicht).RisikoIndex
    };
  }

  getHaftpflichtvariante(fahrzeugartKey: string) {
    if (this.fahrzeugartKeysvarianteA.includes(fahrzeugartKey)) {
      return 'Variante A';
    }
    return 'Variante B';
  }

  kaskoCount(): number {
    return [...(this.kfzResult?.Sparten || [])].filter(x => x.IsKasko).length;
  }

  setZugmaschineInfo(): void {
    const haftpflichtResult = this.kfzResult.Sparten.find(x => x.IsHaftpflicht && x.MotorSteuer === 0);
    const risikoComponent = this.risikoKfzComponents.toArray()[haftpflichtResult.RisikoIndex];
    const fahrzeugart = risikoComponent.fahrzeugart.value;
    this.showZugmaschineInfo = fahrzeugart.Value.includes('Zugmaschine');
  }
}
